import { useUser } from '@/app/redux/hooks/user';
import { Invitation, OrganisationMember } from '@/common/types/organisation';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import validateEmail from '@/common/utils/validateEmail';
import api from '@/db/apiClient';
import { EraserIcon, UploadIcon, WarningIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { AlertCircle, Send, X } from 'lucide-react';
import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

interface EmailTag {
  id: string;
  email: string;
  error?: string;
}

interface EmailInviteDialogProps {
  open: boolean;
  onClose: () => void;
  pendingInvites: Invitation[];
  currentMembers: OrganisationMember[];
  currentUserEmail: string;
  organisationId: string;
  organisationName: string;
}

export default function EmailInviteDialog({
  open,
  onClose,
  pendingInvites,
  currentMembers,
  currentUserEmail,
  organisationId,
  organisationName,
}: EmailInviteDialogProps) {
  const { user } = useUser();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element | string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [emailTags, setEmailTags] = useState<EmailTag[]>([]);

  const updateEmailValidation = (emails: EmailTag[]): EmailTag[] => {
    const seenEmails = new Map<string, string>(); // email -> first id that used it

    return emails.map((tag) => {
      const trimmedEmail = tag.email.toLowerCase();

      // First check all other validation rules
      const basicValidation = validateBasicEmail(trimmedEmail);
      if (basicValidation) {
        return { ...tag, error: basicValidation };
      }

      // Then handle duplicate logic
      if (!seenEmails.has(trimmedEmail)) {
        // This is the first occurrence - mark it as seen and return without error
        seenEmails.set(trimmedEmail, tag.id);
        return { ...tag, error: undefined };
      } else {
        // This is a duplicate - mark it as an error
        return { ...tag, error: 'Email is already in list' };
      }
    });
  };

  const validateBasicEmail = (email: string): string | undefined => {
    const trimmedEmail = email.toLowerCase();

    // Check email format
    if (!validateEmail(trimmedEmail)) {
      return 'Invalid email format';
    }

    // Check if it's the current user
    if (trimmedEmail === currentUserEmail.toLowerCase()) {
      return 'You cannot invite yourself';
    }

    // Check if user is already a member
    if (
      currentMembers.some(
        (member) => member.email.toLowerCase() === trimmedEmail,
      )
    ) {
      return 'This user is already a member';
    }

    // Check if invitation is pending
    if (
      pendingInvites.some(
        (invite) => invite.email.toLowerCase() === trimmedEmail,
      )
    ) {
      return 'An invitation has already been sent to this email';
    }

    return undefined;
  };

  const handleSendInvites = async () => {
    setError(null);

    if (!user) {
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    // If there's text still in the input, convert it to a chip first
    let tagsToSend = emailTags;
    if (inputValue.trim()) {
      const newTag: EmailTag = {
        id: uuidv4(),
        email: inputValue.trim().toLowerCase(),
        error: undefined,
      };
      tagsToSend = updateEmailValidation([...emailTags, newTag]);
      setEmailTags(tagsToSend);
      setInputValue('');
    }

    if (tagsToSend.some((tag) => !!tag.error)) {
      setError('Some emails are invalid. Please fix the errors and try again.');
      return;
    }

    setLoading(true);

    try {
      const validEmails = tagsToSend.filter((tag) => !tag.error);

      for (const [index, tag] of validEmails.entries()) {
        try {
          await api.asEducator.sendInviteEmail({
            organisationId,
            organisationName,
            email: tag.email,
            inviterEmail: currentUserEmail,
            inviterName: user.name,
          });

          trackMixpanelEvent('Invite Sent', {
            organisationId,
            organisationName,
            email: tag.email,
          });
        } catch (error) {
          console.error('Error sending invite email', error);
          setError(UNKNOWN_ERROR_MESSAGE);
          setEmailTags((prev) => prev.slice(0, index));
          break;
        }
      }
      toast.success('Invites sent');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInputValue('');
    setEmailTags([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [open]);

  const handleClearAll = () => {
    setEmailTags([]);
    setInputValue('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processEmails = (text: string): string[] => {
    // Split on commas, newlines, tabs, or multiple spaces
    return text
      .split(/[,\n\t\s]+/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email !== '');
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    trackMixpanelEvent('Uploaded CSV for Invites', {
      organisationId,
      organisationName,
    });
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const csvContent = event.target?.result as string;
      const emails = processEmails(csvContent);

      setEmailTags((prev) => {
        const newTags = [
          ...prev,
          ...emails.map((email) => ({
            id: uuidv4(),
            email: email.trim().toLowerCase(),
            error: undefined, //validate in updateEmailValidation
          })),
        ];
        return updateEmailValidation(newTags);
      });
    };
    reader.readAsText(file);
    setError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    setError(null);

    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail(inputValue.trim());
      return;
    }

    if (e.key === 'Backspace' && inputValue === '' && emailTags.length > 0) {
      const lastTag = emailTags[emailTags.length - 1];
      setInputValue(lastTag.email);
      setEmailTags((prev) => prev.slice(0, -1));
    }
  };

  const addEmail = (email: string) => {
    setError(null);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;

    setEmailTags((prev) => {
      const newTags = [
        ...prev,
        {
          id: uuidv4(),
          email: trimmedEmail,
          error: undefined, // validate in updateEmailValidation
        },
      ];
      return updateEmailValidation(newTags);
    });

    setInputValue('');
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setError(null);

    const pastedText = e.clipboardData!.getData('text');
    const emails = processEmails(pastedText);

    setEmailTags((prev) => {
      const newTags = [
        ...prev,
        ...emails.map((email) => ({
          id: uuidv4(),
          email: email.trim().toLowerCase(),
          error: undefined,
        })),
      ];
      return updateEmailValidation(newTags);
    });
  };

  const handleInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setError(null);

    const value = e.target.value;

    // Check if this is a paste with multiple emails
    if (value.includes(',') || value.includes('\n') || value.includes('\t')) {
      const emails = processEmails(value);
      emails.forEach((email) => {
        if (email) addEmail(email);
      });
      return;
    }

    // Regular single email handling
    const lastChar = value.slice(-1);
    if ([' ', ',', '\n', '\t'].includes(lastChar)) {
      const emailToAdd = value.slice(0, -1).trim();
      if (emailToAdd) {
        addEmail(emailToAdd);
      }
    } else {
      setInputValue(value);
    }
  };

  const removeEmail = (tagId: string) => {
    setError(null);
    setEmailTags((prev) => {
      const newTags = prev.filter((tag) => tag.id !== tagId);
      return updateEmailValidation(newTags);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invite members</DialogTitle>
          <DialogDescription>
            Invite members to your organisation by entering their email
            addresses
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4 ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-3 px-3 py-2 min-h-[100px] border border-input rounded-md shadow-sm placeholder:text-muted-foreground focus-within:ring-1 focus-within:ring-ring">
          {emailTags.map((tag) => (
            <EmailTagChip
              key={tag.id}
              email={tag.email}
              error={tag.error}
              onRemove={() => removeEmail(tag.id)}
            />
          ))}
          <textarea
            value={inputValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              emailTags.length === 0
                ? 'Enter email addresses separated by space, comma, or enter'
                : ''
            }
            className="flex-1 min-w-[200px] text-sm border-none focus:outline-none bg-transparent resize-none"
            rows={1}
          />
        </div>

        <DialogFooter>
          <div className="flex gap-2 mr-auto">
            <input
              type="file"
              accept=".csv"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
            >
              <UploadIcon size={16} />
              Upload CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={handleClearAll}
              disabled={isLoading || (!inputValue && emailTags.length === 0)}
            >
              <EraserIcon size={16} />
              Clear All
            </Button>
          </div>

          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>

          <Button
            onClick={handleSendInvites}
            disabled={!emailTags.length && !inputValue.trim()}
            loading={isLoading}
            className="gap-2"
          >
            <Send size={16} />
            Send Invites
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EmailTagChipProps {
  email: string;
  error?: string;
  onRemove: () => void;
}
function EmailTagChip({ email, error, onRemove }: EmailTagChipProps) {
  const tag = (
    <div
      className={`inline-flex items-center h-6 gap-1.5 px-2 text-sm rounded-sm box-border border ${
        error
          ? 'border border-red-200 text-red-700'
          : 'border border-green-200 text-green-700'
      }`}
    >
      {error && <WarningIcon />}
      <span>{email}</span>
      <button
        onClick={onRemove}
        className="opacity-50 hover:opacity-100 transition-opacity"
        type="button"
      >
        <X size={14} />
      </button>
    </div>
  );

  return error ? (
    <Tooltip>
      <TooltipTrigger asChild>{tag}</TooltipTrigger>
      <TooltipContent>
        <p>{error}</p>
      </TooltipContent>
    </Tooltip>
  ) : (
    tag
  );
}
